import React, {createContext, useContext, useState, ReactNode, useEffect} from 'react';
import {dataStorage, DataStorage} from "../utils/StorageService";
import {useYandexSdk} from "./yandex/yandexSdk";
import {eventEmitter} from "../EventEmitter";
import {usePlayMainSound} from "../hooks/usePlayMainSound";

export type Lang = 'ru' | 'en' | 'tr' | '';
export type ScreenTypes = 'start' | 'game';
export type ModeTypes = 'normal' | 'bomb';

interface AppContextType {
    sound: boolean;
    lang: 'ru' | 'en' | 'tr';
    screen: ScreenTypes;
    mode: ModeTypes;
    settings: boolean;
    ideaIsProcessing: boolean;
    showLeaderBoard: boolean;
    onSetLang: (language: Lang) => void;
    onSwitchSound: () => void;
    onSwitchSettings: () => void;
    onSwitchLeaderBord: () => void;
    onPlayMusic: () => void;
    onStopMusic: () => void;
    setIdeaProcessing: React.Dispatch<React.SetStateAction<boolean>>;
    setScreen: (screen: ScreenTypes, mode: ModeTypes) => void;
    translate: typeof TRANSLATE_LIST['ru'];
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
    children: ReactNode;
}

const AppProvider: React.FC<AppProviderProps> = ({children}) => {
    const {setDataPlayer, yandexSdk} = useYandexSdk();

    const [sound, setSound] = useState(dataStorage.getItemApp()?.sound || false);
    const [lang, setLang] = useState<Lang>(dataStorage.getItemApp()?.language || '');
    const [screen, setScreen] = useState<ScreenTypes>(dataStorage.getItemApp()?.screen || 'start');
    const [mode, setMode] = useState<ModeTypes>(dataStorage.getItemApp()?.mode || 'normal');
    const [settings, setSettings] = useState<boolean>(false);
    const [showLeaderBoard, setShowLeaderBord] = useState(false);
    const [ideaIsProcessing, setIdeaProcessing] = useState(false);

    useEffect(() => {
        if (lang) return;
        const _lang = yandexSdk.environment.i18n.lang;

        if (['ru', 'en', 'tr'].includes(_lang)) {
            setLang(_lang)
            dataStorage.setItemApp({language: _lang});
        } else {
            setLang('en');
            dataStorage.setItemApp({language: 'en'});
        }
    }, [yandexSdk])

    const {play, stop} = usePlayMainSound({sound})

    useEffect(() => {
        const handleDataUpdate = (updatedData: DataStorage) => {
            // console.log('upd', updatedData)
            setDataPlayer(JSON.stringify(updatedData));
        };

        eventEmitter.on('data_update', handleDataUpdate);

        return () => {
            eventEmitter.off('data_update', handleDataUpdate);
        };
    }, [setDataPlayer]);

    const handleSetLang = (language: Lang) => {
        dataStorage.setItemApp({language});
        setLang(language);
    };

    const handleSwitchSound = () => {
        setSound(prev => {
            dataStorage.setItemApp({sound: !prev});
            return !prev;
        });
    };

    const handleSettingsSwitch = () => {
        setSettings(prev => !prev);
    }

    const handleLeaderBordSwitch = () => {
        setShowLeaderBord(prev => !prev);
    }

    return (
        <AppContext.Provider value={{
            sound,
            lang: lang as 'ru' | 'en' | 'tr',
            mode,
            screen,
            settings,
            ideaIsProcessing,
            translate: TRANSLATE_LIST[lang || 'en'],
            showLeaderBoard,
            onSwitchLeaderBord: handleLeaderBordSwitch,
            onSwitchSettings: handleSettingsSwitch,
            onSetLang: handleSetLang,
            onSwitchSound: handleSwitchSound,
            setScreen: (_screen: ScreenTypes, _mode: ModeTypes) => {
                setScreen(_screen);
                setMode(_mode);
                dataStorage.setItemApp({screen: _screen, mode: _mode});
            },
            setIdeaProcessing,
            onPlayMusic: play,
            onStopMusic: stop,
        }}>
            {children}
        </AppContext.Provider>
    );
};

const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};


const TRANSLATE_LIST = {
    ru: {
        end: 'КОНЕЦ ИГРЫ',
        video: 'Посмотрите видео, чтобы удалить 3 ряда!',
        score: 'Счет',
        total: 'Рекорд',
        continue: 'Продолжить',
        leaders: 'ЧЕМПИОНЫ ПО КЛАССИКЕ',
        leadersBomb: 'ЧЕМПИОНЫ ПО БОМБАМ',
        hint: 'Перетаскивайте блоки в сетку, чтобы заполнять и удалять строки и столбцы. Игра закончится, когда на поле не останется свободного места для вставки блоков.',
        classic: 'Классический режим',
        bomb: 'Режим с бомбой',
        hint_bomb: 'Перетаскивайте блоки в сетку, чтобы заполнять и удалять строки и столбцы. Бомбу можно взорвать, если заполнить строку или столбец, в котором она расположена. Игра закончится, когда на поле не останется свободного места для вставки блоков.',
    },
    en: {
        end: 'GAME OVER',
        video: 'Watch the video to remove 3 rows!',
        score: 'Score',
        total: 'Record',
        continue: 'Continue',
        leaders: 'CLASSIC CHAMPIONS',
        leadersBomb: 'BOMB CHAMPIONS',
        hint: 'Drag the blocks into the grid to fill and remove rows and columns. The game will end when there is no more free space on the board to insert blocks.',
        classic: 'Classic mode',
        bomb: 'Bomb mode',
        hint_bomb: 'Drag the blocks into the grid to fill and remove rows and columns. You can detonate the bomb by filling the row or column where it is located. The game will end when there is no more free space on the board to insert blocks.',
    },
    tr: {
        end: 'OYUN BİTTİ',
        video: '3 satırı kaldırmak için videoyu izleyin!',
        score: 'Skor',
        total: 'Rekor',
        continue: 'Devam et',
        leaders: 'KLASİK ŞAMPİYONLAR',
        leadersBomb: 'BOMBA ŞAMPİYONLARI',
        hint: 'Satırları ve sütunları doldurmak ve kaldırmak için blokları ızgaraya sürükleyin. Blok yerleştirmek için tahtada boş yer kalmadığında oyun sona erecek.',
        classic: 'Klasik mod',
        bomb: 'Bomba modu',
        hint_bomb: 'Satırları ve sütunları doldurmak ve kaldırmak için blokları ızgaraya sürükleyin. Bombayı, yer aldığı satırı veya sütunu doldurarak patlatabilirsiniz. Blok yerleştirmek için tahtada boş yer kalmadığında oyun sona erecek.',
    },
}



export {AppProvider, AppContext, useAppContext};
